import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#333333",
    backgroundColor: "#FFFFFF",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
    borderBottom: "2 solid #3B0069",
    paddingBottom: 12,
  },
  titleWrap: {
    flexDirection: "column",
  },
  title: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#3B0069",
    textTransform: "uppercase",
  },
  subtitle: {
    fontSize: 11,
    marginTop: 4,
    color: "#666666",
  },
  orgWrap: {
    width: "42%",
    textAlign: "right",
  },
  orgName: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#3B0069",
    marginBottom: 4,
  },
  orgText: {
    fontSize: 9,
    color: "#666666",
    lineHeight: 1.4,
    marginBottom: 2,
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: "bold",
    backgroundColor: "#F3F4F6",
    padding: 6,
    color: "#3B0069",
    marginBottom: 10,
    textTransform: "uppercase",
  },
  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  infoItem: {
    width: "50%",
    marginBottom: 8,
    paddingRight: 10,
  },
  label: {
    fontSize: 8,
    color: "#6B7280",
    marginBottom: 2,
    textTransform: "uppercase",
  },
  value: {
    fontSize: 10,
    color: "#111827",
    fontWeight: "bold",
  },
  totalBox: {
    border: "1 solid #E5E7EB",
    borderRadius: 4,
    padding: 10,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  totalLabel: {
    fontSize: 10,
    color: "#374151",
  },
  totalValue: {
    fontSize: 14,
    color: "#3B0069",
    fontWeight: "bold",
  },
  remarksBox: {
    marginTop: 8,
    padding: 10,
    borderRadius: 4,
    backgroundColor: "#F9FAFB",
    border: "1 solid #E5E7EB",
  },
  remarksText: {
    fontSize: 9,
    color: "#4B5563",
  },
  footer: {
    position: "absolute",
    bottom: 24,
    left: 40,
    right: 40,
    borderTop: "0.5 solid #E5E7EB",
    paddingTop: 8,
    fontSize: 8,
    color: "#9CA3AF",
    textAlign: "center",
  },
});

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (amount) => {
  const number = Number(amount || 0);
  return `Rs. ${number.toLocaleString("en-IN")}`;
};

const WorkerPaymentReceiptPDFDocument = ({
  transaction,
  worker,
  organization,
  siteData,
}) => {
  const txId = transaction?.transaction_id || String(transaction?.id ?? "").padStart(3, "0");
  const receiptNumber = `WORKER_PAY-${txId}`;
  const workerName = worker?.name || transaction?.worker_name || "N/A";
  const workerContact =
    worker?.contact ||
    transaction?.worker_phone ||
    transaction?.worker_contact ||
    "N/A";
  const paymentDate = transaction?.transaction_date || transaction?.created_on;
  const createdOn = transaction?.created_on || transaction?.transaction_date;
  const comments = transaction?.comments || transaction?.remarks || "";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.titleWrap}>
            <Text style={styles.title}>Payment Receipt</Text>
            <Text style={styles.subtitle}>#{receiptNumber}</Text>
          </View>

          <View style={styles.orgWrap}>
            <Text style={styles.orgName}>
              {organization?.organization_name || "SiteMaster"}
            </Text>
            {organization?.business_address ? (
              <Text style={styles.orgText}>{organization.business_address}</Text>
            ) : null}
            {(organization?.state || organization?.pincode) ? (
              <Text style={styles.orgText}>
                {[organization?.state, organization?.pincode]
                  .filter(Boolean)
                  .join(", ")}
              </Text>
            ) : null}
            {organization?.user_email ? (
              <Text style={styles.orgText}>{organization.user_email}</Text>
            ) : null}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>General Information</Text>
          <View style={styles.infoGrid}>
            <View style={styles.infoItem}>
              <Text style={styles.label}>Worker Name</Text>
              <Text style={styles.value}>{workerName}</Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.label}>Worker Contact</Text>
              <Text style={styles.value}>{workerContact}</Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.label}>Payment Type</Text>
              <Text style={styles.value}>{transaction?.payment_type || "N/A"}</Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.label}>Payment Method</Text>
              <Text style={styles.value}>
                {transaction?.transaction_method || "N/A"}
              </Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.label}>Transaction Date</Text>
              <Text style={styles.value}>{formatDate(paymentDate)}</Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.label}>Created On</Text>
              <Text style={styles.value}>{formatDateTime(createdOn)}</Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.label}>Site</Text>
              <Text style={styles.value}>{siteData?.site_name || "N/A"}</Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={styles.label}>Site Address</Text>
              <Text style={styles.value}>{siteData?.address || "N/A"}</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Payment Summary</Text>
          <View style={styles.totalBox}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Total Paid Amount</Text>
              <Text style={styles.totalValue}>
                {formatCurrency(transaction?.amount)}
              </Text>
            </View>
          </View>

          {comments ? (
            <View style={styles.remarksBox}>
              <Text style={styles.label}>Remarks</Text>
              <Text style={styles.remarksText}>{comments}</Text>
            </View>
          ) : null}
        </View>

        <View style={styles.footer}>
          <Text>
            This is a system-generated receipt. Generated on {formatDateTime(new Date())}.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default WorkerPaymentReceiptPDFDocument;
